// Copyright (c) 2026 Finerty Data Ltd. All rights reserved.
//
// This file is part of proprietary software owned by Finerty Data Ltd.
// It is confidential and may not be copied, distributed, modified, or used,
// in whole or in part, without the prior written consent of Finerty Data Ltd.
//
// Author: Ahmed Hosny

import type { WizardStepId } from '../types/wizard';

interface WizardFooterProps {
  currentStepId: WizardStepId;
  isFirstStep: boolean;
  isLastStep: boolean;
  isSaving: boolean;
  isSubmitting: boolean;
  loaAccepted?: boolean;
  isPolicyClosed?: boolean;
  stepValid?: boolean;
  onBack: () => void;
  onSave: () => void;
  onSaveAndContinue: () => void;
  onComplete: () => void;
}

export function WizardFooter({
  currentStepId,
  isLastStep,
  isSaving,
  loaAccepted,
  stepValid = true,
  onSaveAndContinue,
}: WizardFooterProps) {
  const isStepZero = currentStepId === 'loa-validation';

  if (isStepZero && loaAccepted !== true) {
    return null;
  }

  // Upload documents step has its own action buttons (Scan / Skip).
  if (currentStepId === 'upload-documents') {
    return null;
  }

  // Last step uses WizardCompleteFooter (outside the card)
  if (isLastStep) {
    return null;
  }

  // isLastStep already returned above, so this footer only ever saves-and-continues.
  const loading = isSaving;
  const isDisabled = loading || !stepValid;
  const label = 'Save & Continue';
  const handler = onSaveAndContinue;

  const enabledBg = 'bg-purple-primary cursor-pointer hover:bg-[#1E0633] active:bg-[#150428]';

  const button = (
    <button
      type="button"
      disabled={isDisabled}
      onClick={handler}
      aria-label={isStepZero ? 'Save and continue' : 'Save and continue to next step'}
      className={`h-[48px] px-xl rounded-button border-none transition-colors ${isDisabled ? 'bg-[#E1E1E1] cursor-not-allowed' : enabledBg}`}
    >
      <div className={`font-sans text-body-02 font-medium leading-normal not-italic capitalize ${isDisabled ? 'text-[#888]' : 'text-white'}`}>
        {loading ? 'Loading...' : label}
      </div>
    </button>
  );

  // Default: inline footer inside the form card
  return (
    <div className="flex justify-end mt-lg">
      {button}
    </div>
  );
}

/** Page-level footer bar for closed policy Complete button — rendered outside the white card */
export function WizardCompleteFooter(props: WizardFooterProps) {
  const { isLastStep, isSubmitting, stepValid = true, onComplete } = props;

  const loading = isSubmitting;
  const isDisabled = loading || !stepValid;

  if (!isLastStep) return null;

  return (
    <div className="flex justify-center border-t border-[#E1E1E1] mt-auto bg-white py-md">
      <div className="w-full max-w-[920px] flex justify-end">
      <button
        type="button"
        disabled={isDisabled}
        onClick={onComplete}
        aria-label="Complete and submit all form data"
        className={`h-[48px] px-xl rounded-button border-none transition-colors ${isDisabled ? 'bg-[#E1E1E1] cursor-not-allowed' : 'bg-success-text cursor-pointer hover:bg-[#015528] active:bg-[#014420]'}`}
      >
        <div className={`font-sans text-body-02 font-medium leading-normal not-italic capitalize ${isDisabled ? 'text-[#888]' : 'text-white'}`}>
          {loading ? 'Loading...' : 'Complete'}
        </div>
      </button>
      </div>
    </div>
  );
}
